import type { MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import type { AccountMenuItem } from 'types/navigation';
import { trackAccountMenuEvent } from 'utils/accountMenu';
import useMartyContext from 'hooks/useMartyContext';
import { selectRewardsInfo, selectSharedRewards } from 'selectors/rewards';
import { selectKillswitches } from 'selectors/killswitch';
import LogoutIcon from 'components/common/IconsV2/LogoutIcon';
import { returnToAccountPath } from 'constants/appConstants';
import useMyAccountRedesignEnabled from 'hooks/useMyAccountRedesignEnabled';

const selectAccountMenuRewardsInfo = createSelector([selectRewardsInfo, selectSharedRewards], (rewardsInfo, sharedRewards) => ({
  hasRewardsInfo: !!rewardsInfo,
  canEnroll: rewardsInfo?.canEnroll,
  consented: rewardsInfo?.consented,
  enrolled: rewardsInfo?.enrolled,
  isVip: rewardsInfo && sharedRewards && (rewardsInfo.isVipOrConsented || sharedRewards.isEnrollingRewards)
}));

export const useAccountMenu = (): AccountMenuItem[] => {
  const {
    marketplace: {
      account: { vipDashboardUrl },
      desktopBaseUrl
    }
  } = useMartyContext();

  const rewardsInfo = useSelector(selectAccountMenuRewardsInfo);
  const { showReturnsOrdersDropdownLink } = useSelector(selectKillswitches);

  const { canEnroll, consented, enrolled, hasRewardsInfo, isVip } = rewardsInfo || {};
  const isRewardsMember = (enrolled && consented) || isVip;
  const canEnrollVerbiage = canEnroll && 'Sign up for Zappos VIP';
  const dashBoardLinkText = isRewardsMember ? 'VIP Dashboard' : canEnrollVerbiage;
  const hasRewards = !!dashBoardLinkText || !hasRewardsInfo;
  const rewardsLinkText = dashBoardLinkText || '';

  const isMyAccRedesign = useMyAccountRedesignEnabled();

  const accountMenu: AccountMenuItem[] = [];

  if (hasRewards && !isMyAccRedesign) {
    const rewardsMenuItem: AccountMenuItem = {
      dataTestId: 'headerRewards',
      link: vipDashboardUrl,
      text: rewardsLinkText,
      onClick: () => trackAccountMenuEvent('TE_HEADER_ACCOUNT_MYACCOUNT', 'MyAccount', 'Account Overview')
    };

    accountMenu.push(rewardsMenuItem);
  }

  const accountOverviewMenuItem: AccountMenuItem = {
    dataTestId: 'headerAccountOverview',
    link: returnToAccountPath,
    text: 'Account Overview',
    onClick: () => trackAccountMenuEvent('TE_HEADER_ACCOUNT_REWARDS', 'Rewards', 'Rewards')
  };

  accountMenu.push(accountOverviewMenuItem);

  const ordersMenuItem: AccountMenuItem = {
    dataTestId: 'headerOrderHistory',
    link: '/orders',
    text: showReturnsOrdersDropdownLink ? 'View Orders/Return' : 'Order History',
    onClick: () => trackAccountMenuEvent('TE_HEADER_ACCOUNT_ORDER_HISTORY', 'OrderHistory', 'Order History')
  };

  if (!isMyAccRedesign) {
    accountMenu.push(ordersMenuItem);
  }

  if (hasRewards && isMyAccRedesign) {
    const rewardsMenuItem: AccountMenuItem = {
      dataTestId: 'headerRewards',
      link: vipDashboardUrl,
      text: rewardsLinkText,
      onClick: () => trackAccountMenuEvent('TE_HEADER_ACCOUNT_MYACCOUNT', 'MyAccount', 'Account Overview')
    };

    accountMenu.push(rewardsMenuItem);
  }

  const myOrdersMenuItem: AccountMenuItem = {
    dataTestId: 'headerOrderHistory',
    link: '/my-orders/',
    text: 'My Orders',
    onClick: () => trackAccountMenuEvent('TE_HEADER_ACCOUNT_ORDER_HISTORY', 'OrderHistory', 'My Orders')
  };
  if (isMyAccRedesign && !accountMenu.find(accountItem => accountItem.text === 'My Orders')) {
    accountMenu.push(myOrdersMenuItem);
  }

  const myReturnsMenuItem: AccountMenuItem = {
    dataTestId: 'headerReturnsHistory',
    link: '/my-returns/',
    text: 'My Returns',
    onClick: () => trackAccountMenuEvent('TE_HEADER_ACCOUNT_ORDER_HISTORY', 'OrderHistory', 'My Returns')
  };
  if (isMyAccRedesign && !accountMenu.find(accountItem => accountItem.text === 'My Returns')) {
    accountMenu.push(myReturnsMenuItem);
  }

  const signOutMenuItem: AccountMenuItem = {
    dataTestId: 'headerSignOut',
    Icon: LogoutIcon,
    link: '/logout',
    text: 'Sign Out',
    onClick: (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      trackAccountMenuEvent('TE_HEADER_ACCOUNT_SIGNOUT', 'SignOut', 'Sign Out');
      window.location.href = `${desktopBaseUrl}/logout`;
    }
  };

  accountMenu.push(signOutMenuItem);

  return accountMenu;
};
